import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { tokens } from '@hotjar/hotsauce';

import { getAll as getAllFeatures } from '@hotjar/webapp/domains/common/feature';
import type { Site } from '@hotjar/site';
import { useCurrentSite } from '@hotjar/site';
import { isModal, isOffsite } from '@hotjar/feedback/domain/Surveys/Survey/SurveyLocations';
import { EmbedPreviewFrame } from '@hotjar/feedback/components/Feedback/Wizard/WizardPreview/EmbedPreviewFrame/EmbedPreviewFrame';

import { BrowserFrame } from './components/BrowserFrame';
import { IFrame } from './components/IFrame';
import { ResetButton } from './components/ResetButton';
import { PreviewContent } from './components/PreviewContent';
import type { PreviewData } from './components/PreviewContent';
import { DeviceSelector } from './components/DeviceSelector';
import { ResetConfirmation } from './components/ResetConfirmation';
import { Device, Widget } from './constants';
import type { SurveyDisplayType, FeedbackDisplayType } from './constants';

export interface WidgetConfig {
  activeSeasonalEmotionStyle?: string;
  activeStepInPreview: string | null;
  background: string;
  buttonColor?: string;
  connectVisitData: string;
  content: PreviewData['content'];
  displayType?: SurveyDisplayType | FeedbackDisplayType;
  emotionStyle?: string;
  language: string;
  legalName: string | null;
  logo?: string;
  logoUrl?: string;
  parentElementSelector?: string;
  position: string;
  privacyPolicyUrl: string | null;
  showBranding: boolean;
  showLegal: boolean;
  skin: string;
  widgetLabel: string | null;
}

export interface WidgetPreviewProps {
  hidePreviewActions?: boolean;
  showBrowserFrame?: boolean;
  showDeviceSelector?: boolean;
  widget: Widget;
  widgetConfig: WidgetConfig;
  iframeId?: string;
}

const EmbeddedSize = {
  width: 320,
  height: 223,
};

const Size = Object.freeze({
  [Widget.FEEDBACK]: {
    width: 450,
    height: 500,
  },
  [Widget.SURVEY]: {
    width: 320,
    height: 450,
  },
});

const Container = styled.div`
  text-align: left;
`;

const SimpleFrame = styled.div<{ widget: Widget }>`
  min-width: 200px;
  max-width: ${({ widget }) => (widget === Widget.SURVEY ? '320px' : '390px')};
  margin: 0 auto;
  position: relative;
  text-align: center;
`;

function getPreviewData({
  config: {
    activeSeasonalEmotionStyle,
    activeStepInPreview,
    background,
    buttonColor,
    connectVisitData,
    content,
    displayType,
    emotionStyle,
    language,
    legalName,
    logo,
    logoUrl,
    parentElementSelector,
    position,
    privacyPolicyUrl,
    showBranding,
    showLegal,
    skin,
    widgetLabel,
  },
  currentSite,
  previewDevice,
  widget,
}: {
  config: WidgetConfig;
  currentSite?: Site;
  previewDevice: Device;
  widget: Widget;
}): PreviewData {
  const newContent = { ...content };

  // Set default content.
  switch (widget) {
    case Widget.FEEDBACK:
      if (!newContent.initial) {
        newContent.initial = 'Your invitation message will show here...';
      }
      break;
    default:
    // do nothing
  }

  const previewData: PreviewData = {
    ask_for_consent: connectVisitData === 'ask',
    activeStepInPreview: activeStepInPreview || null,
    background,
    button_color: buttonColor,
    content: newContent,
    display_type: displayType,
    effective_show_branding: showBranding,
    features: getAllFeatures(currentSite || null) || [],
    id: 0,
    language,
    legal_name: legalName,
    logo_url: logo || logoUrl,
    pageBackground: null,
    parent_element_selector: widget === Widget.SURVEY ? undefined : parentElementSelector,
    previewDevice: previewDevice || 'desktop',
    privacy_policy_url: privacyPolicyUrl,
    show_legal: showLegal,
    skin,
    widget_label: widgetLabel,
  };

  if (displayType && isOffsite(displayType as SurveyDisplayType)) {
    previewData.pageBackground = previewData.background;
    previewData.background = '#ffffff';
    previewData.skin = 'light';
  }

  if (widget === Widget.FEEDBACK) {
    previewData.position = position;
    previewData.emotion_style = emotionStyle;
    previewData.active_seasonal_emotion_style = activeSeasonalEmotionStyle;
  }

  return previewData;
}

function getFrameSize(
  widget: Widget,
  displayType?: SurveyDisplayType | FeedbackDisplayType,
  showCustomFrame?: boolean,
): { width: number | string; height: number } {
  if (displayType) {
    if (displayType === 'inline' && showCustomFrame) {
      return EmbeddedSize;
    }
    if (isOffsite(displayType as SurveyDisplayType) || isModal(displayType as SurveyDisplayType)) {
      return { width: '100%', height: Size[widget].height };
    }
  }

  return Size[widget];
}

export const WidgetPreview = ({
  hidePreviewActions,
  showBrowserFrame,
  showDeviceSelector,
  widget,
  widgetConfig,
  iframeId,
}: WidgetPreviewProps): JSX.Element | null => {
  const [previewDevice, setPreviewDevice] = useState(Device.DESKTOP);
  const [shouldReload, setShouldReload] = useState(false);
  const { data: currentSite } = useCurrentSite();
  const { content, position } = widgetConfig;

  useEffect(() => {
    if (!showDeviceSelector && previewDevice !== Device.DESKTOP) {
      setPreviewDevice(Device.DESKTOP);
    }
  }, [previewDevice, showDeviceSelector]);
  if (!content) {
    return null;
  }

  const { width, height } = getFrameSize(widget, widgetConfig.displayType, showBrowserFrame);
  const data = getPreviewData({ config: widgetConfig, currentSite, previewDevice, widget });

  const handleResetClick = () => {
    // Setting shouldReload will trigger a rerender of PreviewContent and a call to `reload()`.
    setShouldReload(true);
  };

  const handleReload = () => {
    setShouldReload(false);
  };
  const CustomFrame = widgetConfig.displayType === 'inline' ? EmbedPreviewFrame : BrowserFrame;
  const FrameWrapper = showBrowserFrame ? CustomFrame : SimpleFrame;

  return (
    <Container>
      {!hidePreviewActions && (
        <ResetButton
          css={css`
            margin-bottom: ${tokens.spacer.x2};
          `}
          onReset={handleResetClick}
        />
      )}
      <FrameWrapper
        backgroundColor={data.pageBackground || undefined}
        displayType={widgetConfig.displayType}
        position={position}
        previewDevice={previewDevice}
        widget={widget}
      >
        <ResetConfirmation isVisible={shouldReload} />
        <IFrame
          width={width}
          height={height}
          iframeId={iframeId}
          isEmbedded={widgetConfig.displayType === 'inline'}
          showCustomFrame={showBrowserFrame}
        >
          {(frameWindow) => (
            <PreviewContent
              data={data}
              frameWindow={frameWindow}
              onReload={handleReload}
              shouldReload={shouldReload}
              widget={widget}
              frameHeight={height}
            />
          )}
        </IFrame>
      </FrameWrapper>
      {!hidePreviewActions && showDeviceSelector && (
        <DeviceSelector onDeviceSelect={setPreviewDevice} selectedDevice={previewDevice} />
      )}
    </Container>
  );
};
